import type { SendEventParams } from '@/utils/sendEvent';
import { PixelResponse } from '../../types';
import { BaseTracker } from './BaseTracker';
import { getCoreVisitorId } from '@/core';

export interface ILeadData extends Record<string, string> {
  email: string;
  name: string;
  phone: string;
  message: string;
}

/**
 * Tracker para eventos de lead
 */
export class LeadTracker extends BaseTracker {
  /**
   * Retorna o payload para envio centralizado no ConversionEngine.
   */
  public getPayload(leadData: Record<string, string>): SendEventParams {
    this.validateRequiredParams(
      leadData,
      this.config.autoTracks?.lead?.requiredFields || []
    );
    return {
      category: 'ENGAGEMENT',
      type: 'LEAD',
      eventData: leadData,
      config: this.config,
      sessionId: this.sessionId,
      version: this.version,
      visitorId: this.visitorId,
    };
  }

  /**
   * Rastreia um lead
   */
  public async track(leadData: Record<string, string>): Promise<PixelResponse> {
    this.validateRequiredParams(
      leadData,
      this.config.autoTracks?.lead?.requiredFields || []
    );
    return this.sendEvent(this.getPayload(leadData));
  }

  /**
   * Rastreia um lead com parâmetros adicionais
   */
  public async trackWithCustomParams(
    leadData: ILeadData,
    customParams: Record<string, unknown>
  ): Promise<PixelResponse> {
    this.validateRequiredParams(
      {
        email: leadData.email,
        name: leadData.name,
        phone: leadData.phone,
        message: leadData.message,
      },
      ['email', 'name', 'phone', 'message']
    );

    return this.sendEvent({
      category: 'ENGAGEMENT',
      type: 'LEAD',
      eventData: {
        email: leadData.email,
        name: leadData.name,
        phone: leadData.phone,
        message: leadData.message,
        ...customParams,
      },
      config: this.config,
      sessionId: this.sessionId,
      version: this.version,
      visitorId: this.visitorId,
    });
  }

  /**
   * Rastreia um lead apenas com email (para casos mais simples)
   */
  public async trackEmailOnly(
    email: string,
    customParams?: Record<string, unknown>
  ): Promise<PixelResponse> {
    this.validateRequiredParams({ email }, ['email']);

    return this.sendEvent({
      category: 'ENGAGEMENT',
      type: 'LEAD',
      eventData: {
        email,
        ...customParams,
      },
      config: this.config,
      sessionId: this.sessionId,
      version: this.version,
      visitorId: this.visitorId,
    });
  }
}

// Função de conveniência para compatibilidade com código existente
export const lead = ({
  email,
  name,
  phone,
  message,
}: ILeadData): Omit<SendEventParams, 'config' | 'sessionId' | 'version'> => {
  const visitorId = getCoreVisitorId();
  if (!visitorId) {
    throw new Error('Visitor ID não encontrado');
  }
  return {
    visitorId: visitorId,
    category: 'ENGAGEMENT',
    type: 'LEAD',
    eventData: {
      email,
      name,
      phone,
      message,
    },
  };
};
